/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Ionicons } from '@expo/vector-icons';
import { Platform, RefreshControl, ScrollView, Text, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { useAuthStore } from '../../../auth/store/useAuthStore';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { CsvImportExportModal } from '../../../shared/csv/CsvImportExportModal';
import { createCatalogCsvAdapter } from '../../infra/catalogCsvAdapter';
import { countBrandsLocal } from '../../infra/brandsRepoRead';
import { countSuppliersLocal } from '../../infra/suppliersRepoRead';
import { listCategories } from '../../infra/categoriesRepoRead';
import type { CatalogCsvEntityKind } from '../../domain/csv/csvEntityKinds';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { useScreenLoadShell } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { CategoriesMasterSection } from './CategoriesMasterSection';
import { BrandsMasterSection } from './BrandsMasterSection';
import { SuppliersMasterSection } from './SuppliersMasterSection';
import type { MasterSectionStats } from './mastersSectionShared';
import { styles } from './CatalogMastersScreen.styles';

type SectionId = 'categories' | 'brands' | 'suppliers';

type SectionMeta = {
  id: SectionId;
  label: string;
  description: string;
  activeLabel: string;
};

const SECTIONS: SectionMeta[] = [
  {
    id: 'categories',
    label: 'Categorías',
    description: 'Administrá las categorías del rubro: activá, ocultá y ordená.',
    activeLabel: 'activas',
  },
  {
    id: 'brands',
    label: 'Marcas',
    description: 'Agrupá productos y guardá el margen sugerido para cada marca.',
    activeLabel: 'activas',
  },
  {
    id: 'suppliers',
    label: 'Proveedores',
    description: 'Tené a mano contacto, condiciones de pago y entrega.',
    activeLabel: 'activos',
  },
];

type SectionStatsMap = Record<SectionId, MasterSectionStats | null>;

const WIDE_LAYOUT_BREAKPOINT = 768;

const SECTION_TO_CSV_ENTITY: Record<SectionId, CatalogCsvEntityKind> = {
  categories: 'categories',
  brands: 'brands',
  suppliers: 'suppliers',
};

type CatalogMastersScreenProps = {
  /** Embebido en modal overlay (sin chrome de shell). */
  embedded?: boolean;
  /** El título lo pone el modal padre. */
  hideTitle?: boolean;
  /** Sección activa al montar (p. ej. al abrir desde atajos del catálogo). */
  initialSection?: SectionId;
};

export const CatalogMastersScreen = ({
  embedded = false,
  /** Título vive en el header del Shell; modals pueden mostrar el propio. */
  hideTitle = true,
  initialSection = 'categories',
}: CatalogMastersScreenProps = {}) => {
  const { width } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isWide = width >= WIDE_LAYOUT_BREAKPOINT;
  const user = useAuthStore((s) => s.user);
  const { entitlements: planEnt } = usePlanEntitlements();

  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const branches = useSessionStore((state) => state.branches);
  const currentBranch = branches.find((branch) => branch.id === currentBranchId);
  const canEdit = useMemo(() => {
    const perm = currentBranch?.permissions?.find((p) => p.module === 'Catalog');
    return perm?.canEdit ?? false;
  }, [currentBranch]);

  const [activeSection, setActiveSection] = useState<SectionId>(initialSection);
  const [csvModalVisible, setCsvModalVisible] = useState(false);
  const [mastersRefreshKey, setMastersRefreshKey] = useState(0);
  const [sectionStats, setSectionStats] = useState<SectionStatsMap>({
    categories: null,
    brands: null,
    suppliers: null,
  });
  const sectionReloadRef = useRef<(() => void) | null>(null);

  const bindSectionReload = useCallback((reload: () => void) => {
    sectionReloadRef.current = reload;
  }, []);

  const loadSectionStats = useCallback(async () => {
    try {
      const [categories, brandsAll, brandsActive, suppliersAll, suppliersActive] = await Promise.all([
        listCategories({ includeInactive: true }),
        countBrandsLocal('all', ''),
        countBrandsLocal('active', ''),
        countSuppliersLocal('all', ''),
        countSuppliersLocal('active', ''),
      ]);
      const categoriesActive = categories.filter((category) => category.isActive).length;
      setSectionStats({
        categories: {
          all: categories.length,
          active: categoriesActive,
          inactive: categories.length - categoriesActive,
        },
        brands: {
          all: brandsAll,
          active: brandsActive,
          inactive: brandsAll - brandsActive,
        },
        suppliers: {
          all: suppliersAll,
          active: suppliersActive,
          inactive: suppliersAll - suppliersActive,
        },
      });
    } catch {
      // Los contadores son contexto del hub; la sección conserva su propio estado de carga/error.
    }
  }, []);

  useEffect(() => {
    void loadSectionStats();
  }, [loadSectionStats]);

  const handleCategoryStatsChange = useCallback((stats: MasterSectionStats) => {
    setSectionStats((current) => ({ ...current, categories: stats }));
  }, []);

  const refreshMasters = useCallback(() => {
    void loadSectionStats();
    if (sectionReloadRef.current) {
      sectionReloadRef.current();
      return;
    }
    setMastersRefreshKey((k) => k + 1);
  }, [loadSectionStats]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'CatalogMasters',
    async () => {
      refreshMasters();
    }
  );

  const sectionContent = useMemo(() => {
    const refreshKey = mastersRefreshKey;
    const bindReload = bindSectionReload;
    switch (activeSection) {
      case 'brands':
        return (
          <BrandsMasterSection
            key={`brands-${refreshKey}`}
            canEdit={canEdit}
            stats={sectionStats.brands}
            onOpenCsv={() => setCsvModalVisible(true)}
            onRecordsChanged={loadSectionStats}
            onBindReload={bindReload}
          />
        );
      case 'suppliers':
        return (
          <SuppliersMasterSection
            key={`suppliers-${refreshKey}`}
            canEdit={canEdit}
            stats={sectionStats.suppliers}
            onOpenCsv={() => setCsvModalVisible(true)}
            onRecordsChanged={loadSectionStats}
            onBindReload={bindReload}
          />
        );
      case 'categories':
      default:
        return (
          <CategoriesMasterSection
            key={`categories-${refreshKey}`}
            canEdit={canEdit}
            onOpenCsv={() => setCsvModalVisible(true)}
            onStatsChange={handleCategoryStatsChange}
            onBindReload={bindReload}
          />
        );
    }
  }, [
    activeSection,
    bindSectionReload,
    canEdit,
    handleCategoryStatsChange,
    loadSectionStats,
    mastersRefreshKey,
    sectionStats.brands,
    sectionStats.suppliers,
  ]);

  const activeMeta = SECTIONS.find((section) => section.id === activeSection) ?? SECTIONS[0];
  const activeStats = sectionStats[activeSection];

  const sidebar = (
    <View style={styles.sidebar}>
      {!hideTitle ? <Text style={styles.sidebarTitle}>Organizar</Text> : null}
      <Text style={styles.sidebarCaption}>Administrar</Text>
      <View style={styles.sidebarNav}>
        {SECTIONS.map((section) => {
          const selected = section.id === activeSection;
          const stats = sectionStats[section.id];
          return (
            <TouchableOpacity
              key={section.id}
              style={[styles.sidebarItem, selected && styles.sidebarItemActive]}
              onPress={() => setActiveSection(section.id)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={section.label}
              testID={`catalog-masters-section-${section.id}`}
            >
              <Text
                style={[styles.sidebarItemText, selected && styles.sidebarItemTextActive]}
                numberOfLines={1}
              >
                {section.label}
              </Text>
              <View style={[styles.sidebarCount, selected && styles.sidebarCountActive]}>
                <Text style={[styles.sidebarCountText, selected && styles.sidebarCountTextActive]}>
                  {stats?.all ?? '—'}
                </Text>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );

  const tabsRow = (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.tabsRowContent}
      style={styles.tabsRow}
    >
      {SECTIONS.map((section) => {
        const selected = section.id === activeSection;
        return (
          <TouchableOpacity
            key={section.id}
            style={[styles.tabChip, selected && styles.tabChipSelected]}
            onPress={() => setActiveSection(section.id)}
            accessibilityRole="button"
            accessibilityState={{ selected }}
            accessibilityLabel={section.label}
            testID={`catalog-masters-tab-${section.id}`}
          >
            <Text style={[styles.tabChipText, selected && styles.tabChipTextSelected]} numberOfLines={1}>
              {section.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );

  return (
    <View
      style={[
        styles.container,
        embedded && styles.containerEmbedded,
        isWide ? styles.containerWide : styles.containerCompact,
      ]}
    >
      {isWide ? sidebar : tabsRow}
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          isWide ? styles.scrollContent : styles.scrollContentCompact,
          {
            paddingBottom: spacing.xxl + insets.bottom + (Platform.OS === 'web' ? 16 : 0),
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        refreshControl={
          Platform.OS === 'web' ? undefined : (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={() => void onRefresh()}
              colors={[ticketColors.ink2]}
              tintColor={ticketColors.ink2}
            />
          )
        }
      >
        <View style={styles.sectionHeader}>
          <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionTitle}>{activeMeta.label}</Text>
            <Text style={styles.sectionDescription}>{activeMeta.description}</Text>
          </View>
          <View style={styles.sectionContext}>
            <View style={[styles.sectionDot, !activeStats && styles.sectionDotMuted]} />
            <Text style={styles.sectionContextText}>
              {activeStats ? `${activeStats.active} ${activeMeta.activeLabel}` : 'Sincronizando…'}
            </Text>
          </View>
        </View>

        {!canEdit ? (
          <View style={styles.readOnlyNotice}>
            <Ionicons name="lock-closed-outline" size={15} color={ticketColors.ink2} />
            <Text style={styles.readOnlyNoticeText}>
              Solo lectura. Podés consultar los datos, pero no cambiar estados ni editar.
            </Text>
          </View>
        ) : null}

        {sectionContent}
      </ScrollView>

      <CsvImportExportModal
        visible={csvModalVisible}
        onClose={() => setCsvModalVisible(false)}
        mode="catalog"
        catalogEntity={SECTION_TO_CSV_ENTITY[activeSection]}
        kicker="Catálogo"
        canEdit={canEdit}
        maxProducts={planEnt?.max_products ?? null}
        performedByUserId={user?.id ?? null}
        onApplied={async () => refreshMasters()}
        adapter={createCatalogCsvAdapter(SECTION_TO_CSV_ENTITY[activeSection], planEnt?.max_products ?? null, user?.id ?? null)}
      />
    </View>
  );
};
